import settings from "../constants/settings";
import { logApiError } from "./observability/logging";
import { GetStaticPaths, GetStaticProps } from "next";

export interface PostResponse {
  id: string;
  type: PostType;
  title: string;
  subtitle: string;
  date_released: string;
  slug: string;
  author?: string;
  location?: string;
  markdown_link: string;
}

export interface PostView {
  id: string;
  type: PostType;
  title: string;
  subtitle: string;
  date_released: string;
  path: string;
  author?: string;
  location?: string;
  markdown?: string;
}

export interface PostsResponse {
  posts: PostResponse[];
}

export enum PostType {
  PRESS = "Press",
  BLOG = "Blog",
}

export interface CreateOrUpdatePostResponse {
  post: PostResponse;
  url: string;
  fields: { [id: string]: string };
}

export const getByLine = (release: PostView | PostResponse) => {
  if (release.author && release.location) {
    return `by ${release.author} ${release.location}`;
  } else {
    return release.location;
  }
};

export const postToPath = (post: PostResponse) => {
  return `/${post.type.toLowerCase()}/${post.slug}`;
};

export interface PostPageProps {
  post: PostView;
}

export const createGetStaticPathsFunction = (
  postType: PostType,
): GetStaticPaths => {
  return async () => {
    try {
      const response = await fetch(
        `${settings.api_url}/posts?post_type=${postType}`,
      );
      const postsResposne = (await response.json()) as PostsResponse;
      const paths = postsResposne.posts.map((post) => ({
        params: { slug: post.slug },
      }));
      return { paths, fallback: "blocking" };
    } catch (err) {
      logApiError(err);
      return { fallback: "blocking", paths: [] };
    }
  };
};

export const getStaticPropsForPostPage: GetStaticProps = async ({ params }) => {
  if (!params || typeof params.slug != "string") {
    return {
      notFound: true,
    };
  }
  try {
    const postsRresponse = await fetch(
      `${settings.api_url}/posts/${params.slug}`,
    );
    if (postsRresponse.status == 404) {
      return {
        notFound: true,
      };
    }
    const post = (await postsRresponse.json()) as PostResponse;
    const markdownResponse = await fetch(post.markdown_link);
    const markdown = await markdownResponse.text();

    return {
      props: {
        post: {
          ...post,
          path: postToPath(post),
          markdown,
        },
      } as PostPageProps,
    };
  } catch (err) {
    logApiError(err);
    return {
      notFound: true,
    };
  }
};

export interface PostsPageProps {
  posts: PostView[];
}

export const getStaticPropsForPostsPage = (
  postType: PostType,
): GetStaticProps => {
  return async () => {
    try {
      const response = await fetch(
        `${settings.api_url}/posts?post_type=${postType}`,
      );
      const postsResponse = (await response.json()) as PostsResponse;

      const updatedBlogposts = postsResponse.posts.map((post) => {
        return {
          ...post,
          path: postToPath(post),
        };
      });

      return {
        props: {
          posts: updatedBlogposts,
        } as PostsPageProps,
      };
    } catch (err) {
      logApiError(err);
      return {
        props: {
          posts: [],
        },
      };
    }
  };
};
